import { useCallback, useState } from "react";
import { ArrowLeft, ClipboardList, Loader2, Search } from "lucide-react";
import { useInfiniteQuery } from "@tanstack/react-query";

import { Money } from "@workspace/accounting/components";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import {
	CardDescription,
	CardHeader,
	CardTitle,
} from "@workspace/ui/components/card";
import { DebouncedInput } from "@workspace/ui/components/debounced-input";
import { FormattedDate } from "@workspace/ui/components/formatted-date";
import { ScrollArea } from "@workspace/ui/components/scroll-area";

import { eposApi } from "../../api";
import { useKioskContext } from "../../kiosk";
import type { Order } from "../../types";
import { useEposContext } from "../epos-context";
import { ClosedOrderDetailDialog } from "./closed-order-detail-dialog";

interface OrdersListViewProps {
	onClose: () => void;
}

function getOrderStatusBadge(status: Order["status"]) {
	switch (status) {
		case "Open":
			return <Badge variant="default">Open</Badge>;
		case "Completed":
			return (
				<Badge variant="secondary" className="bg-green-100 text-green-800">
					Completed
				</Badge>
			);
		case "Paid":
			return (
				<Badge variant="secondary" className="bg-blue-100 text-blue-800">
					Paid
				</Badge>
			);
		case "Refunded":
			return <Badge variant="destructive">Refunded</Badge>;
		case "PartiallyRefunded":
			return (
				<Badge variant="secondary" className="bg-amber-100 text-amber-800">
					Partially Refunded
				</Badge>
			);
		case "Cancelled":
			return <Badge variant="outline">Cancelled</Badge>;
		default:
			return <Badge variant="outline">{status}</Badge>;
	}
}

export function OrdersListView({ onClose }: OrdersListViewProps) {
	const { timeZone, tenant } = useEposContext();
	const { venueId } = useKioskContext();

	const [searchQuery, setSearchQuery] = useState("");
	const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

	const searchParam = searchQuery.length >= 2 ? searchQuery : undefined;

	const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
		useInfiniteQuery({
			...eposApi.searchClosedOrdersOptions(tenant, venueId, {
				pageSize: 25,
				query: searchParam,
			}),
			initialPageParam: 1,
			getNextPageParam: (lastPage) => {
				if (!lastPage.hasNextPage) return undefined;
				return lastPage.page + 1;
			},
		});

	const allItems = data?.pages.flatMap((page) => page.items) ?? [];
	const totalCount = data?.pages[0]?.count ?? 0;

	const handleSearchChange = useCallback((value: string | number) => {
		setSearchQuery(String(value));
	}, []);

	return (
		<div className="flex h-full flex-col">
			<CardHeader className="pb-3">
				<div className="flex items-center justify-between">
					<div>
						<CardTitle className="flex items-center gap-2 text-lg">
							<ClipboardList className="size-5" />
							Order History
						</CardTitle>
						<CardDescription>
							{totalCount} closed order
							{totalCount !== 1 ? "s" : ""}
						</CardDescription>
					</div>
					<Button variant="outline" size="sm" onClick={onClose}>
						<ArrowLeft className="mr-2 size-4" />
						Back
					</Button>
				</div>
			</CardHeader>

			{/* Search */}
			<div className="border-t px-4 py-3">
				<div className="relative">
					<Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
					<DebouncedInput
						value={searchQuery}
						onChange={handleSearchChange}
						wait={300}
						placeholder="Search by order ID, tab name or number..."
						className="pl-9"
					/>
				</div>
			</div>

			<ScrollArea className="flex-1 border-t">
				{isFetching && allItems.length === 0 ? (
					<div className="flex items-center justify-center p-8">
						<Loader2 className="size-6 animate-spin text-muted-foreground" />
					</div>
				) : allItems.length === 0 ? (
					<div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
						<ClipboardList className="mb-4 size-12 opacity-50" />
						<p className="text-lg font-medium">No orders found</p>
						<p className="text-sm">
							{searchQuery
								? "Try adjusting your search"
								: "Closed orders will appear here"}
						</p>
					</div>
				) : (
					<div className="divide-y">
						{allItems.map((order) => (
							<button
								key={order.id}
								type="button"
								className="w-full p-4 space-y-0.5 text-left hover:bg-muted/50 transition-colors"
								onClick={() => setSelectedOrderId(order.id)}
							>
								<div className="flex items-center justify-between">
									<div className="flex flex-wrap items-center gap-2">
										<span className="font-medium">
											#{order.id.slice(-6)}
											{order.tabName && (
												<span className="text-muted-foreground">
													{" "}
													&middot; {order.tabName}
												</span>
											)}
											{!order.tabName && order.tabNumber && (
												<span className="text-muted-foreground">
													{" "}
													&middot; Tab #{order.tabNumber}
												</span>
											)}
										</span>
										{getOrderStatusBadge(order.status)}
									</div>
								</div>
								<div className="text-sm text-muted-foreground">
									<Money {...order.total} /> &middot;{" "}
									<FormattedDate
										date={order.completedAt ?? order.createdAt}
										timeZone={timeZone}
										options={{
											day: "2-digit",
											month: "2-digit",
											year: "numeric",
										}}
									/>{" "}
									<FormattedDate
										date={order.completedAt ?? order.createdAt}
										timeZone={timeZone}
										options={{
											hour: "2-digit",
											minute: "2-digit",
											hour12: false,
										}}
									/>
									{order.closeReason && <> &middot; {order.closeReason}</>}
								</div>
							</button>
						))}

						{hasNextPage && (
							<div className="p-4">
								<Button
									variant="outline"
									className="w-full"
									onClick={() => fetchNextPage()}
									disabled={isFetchingNextPage}
								>
									{isFetchingNextPage ? (
										<Loader2 className="mr-2 size-4 animate-spin" />
									) : null}
									Load more
								</Button>
							</div>
						)}
					</div>
				)}
			</ScrollArea>

			<ClosedOrderDetailDialog
				orderId={selectedOrderId}
				onClose={() => setSelectedOrderId(null)}
			/>
		</div>
	);
}
